<template>
  <div class="sleep-manual">
    <div class="shell" :aria-hidden="preview ? 'true' : null">
      <header class="topbar">
        <router-link v-if="topic === 'home'" :to="manualLink('home')" class="brand">八戒睡眠</router-link>
        <router-link v-else :to="manualLink('home')" class="back">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 5-7 7 7 7" /></svg>
          返回目录
        </router-link>
        <span class="top-title">{{ title }}</span>
      </header>

      <main ref="main">
        <section v-show="topic === 'home'" class="home-view">
          <div class="product">
            <h1 tabindex="-1">睡眠管家<br><span>使用说明</span></h1>
            <img
              v-if="!deviceImageFailed"
              :src="deviceImage.src"
              :width="deviceImage.width"
              :height="deviceImage.height"
              alt="睡眠管家主机正面"
              fetchpriority="high"
              @error="deviceImageFailed = true"
            >
            <p v-else class="device-image-error" role="status">主机图片未能加载，请重新打开页面。</p>
          </div>
          <router-link :to="manualLink('start')" class="primary-entry">
            <span>第一次使用<small>摆放、连接与开始睡眠</small></span>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
          </router-link>
          <nav class="topics" aria-label="说明目录">
            <router-link v-for="item in topics" :key="item.key" :to="manualLink(item.key)">
              <span>{{ item.title }}<small>{{ item.description }}</small></span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
            </router-link>
          </nav>
          <router-link :to="manualLink('manual')" class="manual-entry">
            完整说明书
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
          </router-link>
          <p class="home-note">智能健康电子产品，非医疗器械。</p>
        </section>
        <sleep-manual-content :topic="topic" @preview="openPreview" />
      </main>
    </div>

    <div v-if="preview" class="sleep-manual-preview" role="dialog" aria-modal="true" aria-labelledby="manual-image-caption">
      <van-image-preview
        :value="true"
        :images="[preview.src]"
        :show-index="false"
        :close-on-popstate="true"
        @input="closePreview"
      >
        <template #cover>
          <div class="manual-preview-header">
            <p id="manual-image-caption">{{ preview.caption }}<small>双指缩放查看图片</small></p>
            <button ref="closePreviewButton" type="button" aria-label="关闭图片" @click="closePreview">关闭 ×</button>
          </div>
        </template>
      </van-image-preview>
    </div>
  </div>
</template>

<script>
import { ImagePreview } from 'vant'
import SleepManualContent from '@/components/sleep-manual/SleepManualContent.vue'
import images from '@/components/sleep-manual/images'
import { getSleepManualTopic, sleepManualLocation, SLEEP_MANUAL_TITLES } from '@/utils/sleep-manual'

export default {
  name: 'SleepManual',
  components: { SleepManualContent, VanImagePreview: ImagePreview.Component },
  data () {
    return {
      deviceImage: images.device,
      deviceImageFailed: false,
      preview: null,
      positions: {},
      topics: [
        { key: 'use', title: '日常使用', description: '方案调整、呼吸练习与语音控制' },
        { key: 'faq', title: '常见问题', description: '联网、监测与设备异常' },
        { key: 'safety', title: '安全与清洁', description: '使用须知、清洁与芳香耗材' },
        { key: 'service', title: '参数与售后', description: '包装清单、产品参数与保修' }
      ]
    }
  },
  computed: {
    topic () { return getSleepManualTopic(this.$route.params.section) },
    title () { return SLEEP_MANUAL_TITLES[this.topic] || '页面未找到' }
  },
  watch: {
    '$route.fullPath' () { this.restoreReadingPosition(true) }
  },
  mounted () {
    window.addEventListener('keydown', this.onPreviewKeydown)
    this.restoreReadingPosition(false)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onPreviewKeydown)
    if (this.restoreFrame) window.cancelAnimationFrame(this.restoreFrame)
  },
  beforeRouteUpdate (to, from, next) {
    this.positions[this.topic] = window.pageYOffset
    this.closePreview()
    next()
  },
  beforeRouteLeave (to, from, next) {
    this.closePreview()
    next()
  },
  methods: {
    manualLink (topic) { return sleepManualLocation(topic, this.$route.query) },
    restoreReadingPosition (focusHeading) {
      document.title = `睡眠管家 · ${this.title}`
      this.$nextTick(() => {
        if (this.restoreFrame) window.cancelAnimationFrame(this.restoreFrame)
        this.restoreFrame = window.requestAnimationFrame(() => {
          if (focusHeading) {
            const section = this.$refs.main.querySelector('.home-view:not([style*="display: none"]), .article-view:not([style*="display: none"])')
            const heading = section && section.querySelector('h1')
            if (heading) heading.focus({ preventScroll: true })
          }
          window.scrollTo(0, this.positions[this.topic] || 0)
        })
      })
    },
    openPreview (image) {
      this.preview = image
      this.$nextTick(() => {
        if (this.$refs.closePreviewButton) this.$refs.closePreviewButton.focus({ preventScroll: true })
      })
    },
    closePreview () {
      if (!this.preview) return
      const trigger = this.preview.trigger
      this.preview = null
      this.$nextTick(() => {
        if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true })
      })
    },
    onPreviewKeydown (event) {
      if (!this.preview) return
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closePreview()
      } else if (event.key === 'Tab') {
        // The visible close button is the dialog's only keyboard control.
        event.preventDefault()
        this.$refs.closePreviewButton.focus({ preventScroll: true })
      }
    }
  }
}
</script>

<style lang="scss">
@use '@/assets/css/sleep-manual.scss';
</style>
